import React from 'react';
import { motion } from 'framer-motion';
import { ThermometerIcon, DropletsIcon, WindIcon, InfoIcon, AlertTriangleIcon, BotIcon, FlaskConicalIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
interface RoomZonesSidebarProps {
  selectedRoom: string;
  onSelectRoom: (id: string) => void;
  rooms: Room[];
  humidity?: number;
  aqi?: number;
}
interface AgentMessage {
  id: string;
  type: 'info' | 'warning';
  title: string;
  description: string;
}
const AGENT_MESSAGES: AgentMessage[] = [{
  id: 'msg1',
  type: 'info',
  title: '새로운 채광 관리 제안을 드려요',
  description: '지금 거실의 블라인드를 조금 더 열어주세요. 집사의 계산결과...'
}, {
  id: 'msg2',
  type: 'info',
  title: '새로운 에어컨 관리 제안을 드려요',
  description: '집사의 계산결과, 지금 안방의 시스템 에어컨을 22°C로 맞추면 쾌적...'
}, {
  id: 'msg3',
  type: 'warning',
  title: '안방의 에어드레서 사용량이 과다합니다.',
  description: '현재 안방의 에어드레서는 16시간 동안 평균 100Wh의 전기를 사용...'
}, {
  id: 'msg4',
  type: 'info',
  title: '내일의 에너지 관리 제안을 드려요',
  description: '한번 확인해주시겠습니까?'
}];
export function RoomZonesSidebar({
  selectedRoom,
  onSelectRoom,
  rooms,
  humidity = 48,
  aqi = 35
}: RoomZonesSidebarProps) {
  // Calculate average temp
  const avgTemp = (rooms.reduce((sum, r) => sum + r.temp, 0) / rooms.length).toFixed(1);
  // AQI status
  const aqiStatus = aqi <= 50 ? '좋음' : aqi <= 100 ? '무난함' : '좋지않음';
  const aqiColor = aqi <= 50 ? 'text-emerald-400' : aqi <= 100 ? 'text-amber-400' : 'text-red-400';
  const aqiBg = aqi <= 50 ? 'bg-emerald-400' : aqi <= 100 ? 'bg-amber-400' : 'bg-red-400';
  // Humidity status
  const humidityOk = humidity >= 30 && humidity <= 60;
  const humidityColor = humidityOk ? 'text-cyan-400' : 'text-amber-400';
  // TVOC status
  const tvoc = 185;
  const tvocStatus = tvoc < 200 ? '좋음' : tvoc < 400 ? '보통' : '나쁨';
  const tvocColor = tvoc < 200 ? 'text-emerald-400' : tvoc < 400 ? 'text-amber-400' : 'text-red-400';
  const tvocBg = tvoc < 200 ? 'bg-emerald-400' : tvoc < 400 ? 'bg-amber-400' : 'bg-red-400';
  return <div className="w-64 flex-shrink-0 flex flex-col gap-2 p-3 border-r border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-188">
      {/* Top Widgets: Avg Temp, Humidity, Air Quality */}
      <div className="flex flex-col gap-2" data-id="element-189">
        {/* Avg Temp */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5" data-id="element-190">
          <div className="flex items-center justify-between" data-id="element-191">
            <div className="flex items-center space-x-2" data-id="element-192">
              <ThermometerIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-193" />
              <span className="text-[11px] text-slate-400 uppercase tracking-wider font-bold" data-id="element-194">
                실내온도
              </span>
            </div>
            <span className="text-base font-mono font-bold text-white glow-text-amber" data-id="element-195">
              25°C
            </span>
          </div>
          <div className="w-full bg-slate-800 h-1 mt-1.5 rounded-full overflow-hidden" data-id="element-196">
            <div className="bg-gradient-to-r from-cyan-500 to-amber-500 h-full w-3/4 opacity-70" data-id="element-197" />
          </div>
        </motion.div>

        {/* Humidity */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5" data-id="element-198">
          <div className="flex items-center justify-between" data-id="element-199">
            <div className="flex items-center space-x-2" data-id="element-200">
              <DropletsIcon className={`w-3.5 h-3.5 ${humidityColor}`} data-id="element-201" />
              <span className="text-[11px] text-slate-400 uppercase tracking-wider font-bold" data-id="element-202">
                실내습도
              </span>
            </div>
            <span className={`text-base font-mono font-bold ${humidityColor}`} data-id="element-203">
              {humidity}%
            </span>
          </div>
          <div className="w-full bg-slate-800 h-1 mt-1.5 rounded-full overflow-hidden" data-id="element-204">
            <div className={`h-full rounded-full transition-all duration-1000 ${humidityOk ? 'bg-cyan-400' : 'bg-amber-400'}`} style={{
            width: `${humidity}%`
          }} data-id="element-205" />
          </div>
          <div className="mt-0.5 text-[9px] text-slate-600 text-right font-mono" data-id="element-206">
            {humidityOk ? '적절함' : '조절중'}
          </div>
        </motion.div>

        {/* Air Quality */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5" data-id="element-207">
          <div className="flex items-center justify-between" data-id="element-208">
            <div className="flex items-center space-x-2" data-id="element-209">
              <WindIcon className={`w-3.5 h-3.5 ${aqiColor}`} data-id="element-210" />
              <span className="text-[11px] text-slate-400 uppercase tracking-wider font-bold" data-id="element-211">
                공기질
              </span>
            </div>
            <div className="flex items-center space-x-1.5" data-id="element-212">
              <span className={`text-base font-mono font-bold ${aqiColor}`} data-id="element-213">
                {aqi}
              </span>
              <span className={`text-[8px] font-bold uppercase tracking-wider ${aqiColor} opacity-80`} data-id="element-214">
                {aqiStatus}
              </span>
            </div>
          </div>
          <div className="w-full bg-slate-800 h-1 mt-1.5 rounded-full overflow-hidden" data-id="element-215">
            <div className={`h-full ${aqiBg} transition-all duration-1000 relative`} style={{
            width: `${aqi / 200 * 100}%`
          }} data-id="element-216">
              <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-white/50" data-id="element-217" />
            </div>
          </div>
          <div className="flex justify-between mt-0.5 text-[8px] text-slate-600 font-mono" data-id="element-218">
            <span data-id="element-219">PM2.5: 12</span>
            <span data-id="element-220">CO2: 450</span>
          </div>
        </motion.div>

        {/* TVOC */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-2.5 rounded-xl bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/5" data-id="element-221">
          <div className="flex items-center justify-between" data-id="element-222">
            <div className="flex items-center space-x-2" data-id="element-223">
              <FlaskConicalIcon className={`w-3.5 h-3.5 ${tvocColor}`} data-id="element-224" />
              <span className="text-[11px] text-slate-400 uppercase tracking-wider font-bold" data-id="element-225">
                TVOC
              </span>
            </div>
            <div className="flex items-center space-x-1.5" data-id="element-226">
              <span className={`text-base font-mono font-bold ${tvocColor}`} data-id="element-227">
                {tvoc}
              </span>
              <span className="text-[8px] text-slate-500 font-mono" data-id="element-228">ppb</span>
              <span className={`text-[8px] font-bold uppercase tracking-wider ${tvocColor} opacity-80`} data-id="element-229">
                {tvocStatus}
              </span>
            </div>
          </div>
          <div className="w-full bg-slate-800 h-1 mt-1.5 rounded-full overflow-hidden" data-id="element-230">
            <div className={`h-full ${tvocBg} transition-all duration-1000 relative`} style={{
            width: `${Math.min(tvoc / 600 * 100, 100)}%`
          }} data-id="element-231">
              <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-white/50" data-id="element-232" />
            </div>
          </div>
          <div className="flex justify-between mt-0.5 text-[8px] text-slate-600 font-mono" data-id="element-233">
            <span data-id="element-234">휘발성유기화합물</span>
            <span data-id="element-235">기준: 400ppb</span>
          </div>
        </motion.div>
      </div>

      {/* Divider */}
      <div className="h-px bg-white/5 my-0.5" data-id="element-236" />

      {/* SED Agent Header */}
      <div className="flex items-center gap-2 px-1" data-id="element-237">
        <BotIcon className="w-3.5 h-3.5 text-cyan-400" data-id="element-238" />
        <span className="text-[12px] font-mono uppercase tracking-widest text-slate-500" data-id="element-239">
          SED집사
        </span>
        <span className="ml-auto text-[8px] font-mono text-cyan-400/60 bg-cyan-400/10 px-1.5 py-0.5 rounded-full border border-cyan-400/20" data-id="element-240">
          {AGENT_MESSAGES.length}건
        </span>
      </div>

      {/* Agent Message Cards - Compact */}
      <div className="flex flex-col space-y-1.5 overflow-y-auto flex-1 min-h-0" data-id="element-241">
        {AGENT_MESSAGES.slice(0, 4).map((msg, index) => {
        const isWarning = msg.type === 'warning';
        return <motion.div key={msg.id} initial={{
          opacity: 0,
          x: -15
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4 + index * 0.08
        }} className={`
                group relative flex items-start gap-2 p-2 rounded-lg border transition-all duration-300 w-full text-left overflow-hidden cursor-default
                ${isWarning ? 'bg-amber-500/5 border-amber-500/30' : 'bg-white/[0.02] border-white/5'}
              `} data-id="element-242">
              {/* Left accent bar */}
              <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${isWarning ? 'bg-amber-400' : 'bg-cyan-400/40'}`} data-id="element-243" />

              {/* Icon */}
              <div className={`p-1 rounded-md flex-shrink-0 ${isWarning ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/10 text-cyan-400'}`} data-id="element-244">
                {isWarning ? <AlertTriangleIcon className="w-3 h-3" data-id="element-245" /> : <InfoIcon className="w-3 h-3" data-id="element-246" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-247">
                <div className={`text-[10px] font-medium leading-snug mb-0.5 ${isWarning ? 'text-amber-200' : 'text-slate-300'}`} data-id="element-248">
                  {msg.title}
                </div>
                <div className="text-[9px] text-slate-500 leading-relaxed line-clamp-1" data-id="element-249">
                  {msg.description}
                </div>
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}